import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptNightInvitation,
  createNightShareInvitation,
  declineNightInvitation,
  deleteNight,
  downloadNightCalendarFile,
  fetchNight,
  fetchNightPosition,
  fetchNights,
  removeNightCalendar,
  syncNightCalendar,
} from '../api/nights';
import { useToast } from '../components/tavern/ToastProvider';
import { nightInvitationResponseMessage } from '../utils/apiErrorMessages';
import ReportIssueButton from '../components/support/ReportIssueButton';
import { supportContextFromApiError } from '../utils/supportReports';
import {
  formatDisplayDate,
  formatDisplayTime,
  formatDisplayWeekday,
  timeInputValueFromDateTime,
} from '../i18n/formatters';
import { NightContributionsPanel, NightVotesPanel } from '../components/nights/NightSummaryPanels';
import TavernChatPanel from '../components/chat/TavernChatPanel';
import { FUTURE_NIGHTS_PAGE_SIZE, PAST_NIGHTS_PAGE_SIZE } from '../utils/nightPagination';
import { nightCardStatus } from '../utils/nightStatus';

const highlightPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(236, 170, 65, 0.68), 0 20px 42px -34px rgba(6, 37, 34, 0.5);
    transform: translateY(0);
  }
  22% {
    box-shadow: 0 0 0 8px rgba(236, 170, 65, 0.2), 0 24px 50px -32px rgba(6, 37, 34, 0.58);
    transform: translateY(-2px);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(236, 170, 65, 0), 0 20px 42px -34px rgba(6, 37, 34, 0.5);
    transform: translateY(0);
  }
`;

const textWrapSx = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const invitationStatusForPerson = (night, person) => {
  if (!night || !person) return 'accepted';
  if (Number(person.id) === Number(night.hostId)) return 'accepted';

  const invitations = night.invitations || {};
  const personId = Number(person.id);

  if ((invitations.accepted || []).some((item) => Number(item.id) === personId)) return 'accepted';
  if ((invitations.declined || []).some((item) => Number(item.id) === personId)) return 'declined';
  if ((invitations.pending || []).some((item) => Number(item.id) === personId)) return 'pending';

  return invitations.requiresResponse ? 'pending' : 'accepted';
};

const invitationStatusLabelKey = (status) => {
  if (status === 'declined') return 'nights.invitations.declinedInvitations';
  if (status === 'pending') return 'nights.invitations.waitingForResponse';
  return 'nights.invitations.confirmedParticipants';
};

const invitationAvatarSx = (status, outlineColor = 'rgba(255,248,232,0.88)') => ({
  border: '3px solid',
  borderColor:
    status === 'declined'
      ? 'error.main'
      : status === 'pending'
        ? 'text.disabled'
        : 'success.main',
  outline: '2px solid',
  outlineColor,
  opacity: status === 'declined' ? 0.58 : 1,
});

export const NIGHT_FOCUS_SCROLL_OPTIONS = Object.freeze({
  behavior: 'smooth',
  block: 'start',
});

const PAST_SECTION_SCROLL_OPTIONS = Object.freeze({
  behavior: 'smooth',
  block: 'start',
});

const nightFocusScrollTargetSx = {
  scrollMarginTop: {
    xs: 'calc(70px + env(safe-area-inset-top, 0px) + 12px)',
    md: 16,
  },
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const nightFocusIdFromQuery = (query) => {
  const focusNightId = Number(query.get('focusNightId'));
  if (Number.isInteger(focusNightId) && focusNightId > 0) return focusNightId;

  const highlightedNightId = Number(query.get('highlightNight'));
  if (Number.isInteger(highlightedNightId) && highlightedNightId > 0) {
    return highlightedNightId;
  }

  return null;
};

export const normalizeNightListForRender = (items = []) =>
  asArray(items)
    .map((night) => {
      if (!night || typeof night !== 'object') return null;

      const id = Number(night.id);
      if (!Number.isInteger(id) || id <= 0) return null;

      return {
        ...night,
        id,
        assignments: asArray(night.assignments),
        availableGames: asArray(night.availableGames),
        calendar:
          night.calendar && typeof night.calendar === 'object' && !Array.isArray(night.calendar)
            ? night.calendar
            : null,
        companions: asArray(night.companions).filter(Boolean),
        games: asArray(night.games),
        host: night.host && typeof night.host === 'object' ? night.host : null,
        permissions:
          night.permissions && typeof night.permissions === 'object'
            ? night.permissions
            : {},
        voting:
          night.voting && typeof night.voting === 'object' && !Array.isArray(night.voting)
            ? night.voting
            : null,
      };
    })
    .filter(Boolean);

const nightScheduleTime = (night) => {
  const timestamp = new Date(night?.scheduledAt).getTime();

  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const futurePageCount = (total) =>
  Math.max(1, Math.ceil((Number(total) || 0) / FUTURE_NIGHTS_PAGE_SIZE));

const pastPageCount = (total) =>
  Math.max(1, Math.ceil((Number(total) || 0) / PAST_NIGHTS_PAGE_SIZE));

export const nightShareUrl = (token, origin = window.location.origin) =>
  `${origin}/invite/gathering/${encodeURIComponent(String(token || ''))}`;

export const nightShareText = ({ inviterName, t, title, url }) =>
  t('nights.share.text', {
    inviterName: inviterName || t('profile.defaults.displayName'),
    title,
    url,
  });

export const canShowNightVoteAction = (night, archived = false) =>
  !archived &&
  (night?.permissions?.canVote === true ||
    night?.permissions?.canManageGames === true ||
    night?.hostId === night?.viewerId);

export const mergeFocusedNightForRender = (current = [], night) => {
  const [normalized] = normalizeNightListForRender([night]);
  if (!normalized) return current;

  return [...asArray(current).filter((item) => Number(item?.id) !== normalized.id), normalized]
    .sort(
      (left, right) =>
        nightScheduleTime(left) - nightScheduleTime(right) ||
        Number(left.id) - Number(right.id)
    );
};

export const shouldRecoverFocusedNight = ({
  focusedNightLoaded,
  highlightedNightId,
  loading,
}) => Boolean(highlightedNightId) && !loading && !focusedNightLoaded;

const NightCard = ({
  archived = false,
  highlighted = false,
  labelKey,
  language,
  night,
  onAcceptInvitation,
  onCancel,
  onDeclineInvitation,
  onShareNight,
  onRemoveCalendar,
  onSyncCalendar,
  onDownloadCalendarFile,
  onOpenChat,
  onNavigate,
  responseLoading,
  shareLoading,
  calendarLoading,
  calendarFileLoading,
  t,
}) => {
  const attendees = [night.host, ...night.companions].filter(Boolean);
  const tableAttendees = attendees.map((person) => ({
    ...person,
    invitationStatus: invitationStatusForPerson(night, person),
  }));
  const canEdit = !archived && (night.permissions?.canEdit ?? night.hostId === night.viewerId);
  const canManageGames =
    !archived && (night.permissions?.canManageGames ?? night.hostId === night.viewerId);
  const canVote = canShowNightVoteAction(night, archived);
  const invitationStatus = night.invitations?.viewerStatus || null;
  const awaitingResponse = !archived && invitationStatus === 'pending';
  const calendar = night.calendar || {};
  const calendarEvent = calendar.event || null;
  const canUseCalendar = !archived && !awaitingResponse && (calendar.connected || calendar.needsReconnect);
  const canDownloadCalendarFile = !archived && !awaitingResponse;
  const canShareNight = canEdit && !awaitingResponse;
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const actionMenuOpen = Boolean(actionAnchorEl);
  const gatheringDate = formatDisplayDate(night.scheduledAt, language);
  const gatheringWeekday = formatDisplayWeekday(night.scheduledAt, language);
  const gatheringTime = formatDisplayTime(timeInputValueFromDateTime(night.scheduledAt), language);
  const shareTitle = t('nights.share.title', { title: night.title });
  const closeActionMenu = () => setActionAnchorEl(null);

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: 'background.paper',
        borderColor: highlighted ? 'secondary.main' : 'divider',
        opacity: archived ? 0.86 : 1,
        animation: highlighted ? `${highlightPulse} 1800ms ease-out 2` : 'none',
        '&::after': highlighted
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              borderRadius: 'inherit',
              background: `linear-gradient(90deg, ${alpha('#ECAA41', 0.2)}, transparent 34%)`,
              opacity: 0.78,
            }
          : undefined,
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.1fr' },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            p: { xs: 3.5, md: 5 },
            minHeight: 310,
            color: archived ? 'text.primary' : '#fff8e8',
            bgcolor: archived ? 'rgba(15, 76, 69, 0.08)' : 'primary.dark',
            borderRight: { md: '1px solid' },
            borderBottom: { xs: '1px solid', md: 0 },
            borderColor: 'divider',
            background:
              archived
                ? 'linear-gradient(135deg, rgba(255,250,240,0.68), rgba(15,76,69,0.08))'
                : 'radial-gradient(circle at 85% 95%, rgba(215,160,75,0.22), transparent 45%), #0B3A35',
            minWidth: 0,
            position: 'relative',
          }}
        >
          {canEdit || canShareNight ? (
            <Box
              sx={{
                position: 'absolute',
                top: 14,
                right: 14,
                zIndex: 2,
              }}
            >
              <Tooltip title={t('common.actions')}>
                <IconButton
                  aria-label={t('common.actions')}
                  aria-controls={actionMenuOpen ? `night-actions-${night.id}` : undefined}
                  aria-haspopup="menu"
                  aria-expanded={actionMenuOpen ? 'true' : undefined}
                  onClick={(event) => setActionAnchorEl(event.currentTarget)}
                  sx={{
                    bgcolor: 'rgba(255, 248, 232, 0.94)',
                    color: 'primary.main',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'secondary.light' },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                id={`night-actions-${night.id}`}
                anchorEl={actionAnchorEl}
                open={actionMenuOpen}
                onClose={closeActionMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {canShareNight ? (
                  <MenuItem
                    disabled={shareLoading}
                    onClick={() => {
                      closeActionMenu();
                      onShareNight?.({ night, title: shareTitle });
                    }}
                  >
                    <ListItemIcon>
                      <ShareIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nights.actions.shareNight')}
                  </MenuItem>
                ) : null}
                {canEdit ? (
                  <MenuItem
                    onClick={() => {
                      closeActionMenu();
                      onNavigate(`/nights/${night.id}/edit`);
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nights.actions.editNight')}
                  </MenuItem>
                ) : null}
                {canEdit ? (
                  <MenuItem
                    onClick={() => {
                      closeActionMenu();
                      onCancel(night);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nights.actions.cancelNight')}
                  </MenuItem>
                ) : null}
              </Menu>
            </Box>
          ) : null}

          <Typography
            variant="subtitle2"
            sx={{
              color: archived ? 'secondary.dark' : 'secondary.light',
              mb: 2,
              pr: canEdit || canShareNight ? 6 : 0,
              ...textWrapSx,
            }}
          >
            {t(labelKey)}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              color: archived ? 'text.primary' : 'inherit',
              fontSize: { xs: 32, md: 42 },
              mb: 4,
              pr: { xs: 7, md: 2 },
              ...textWrapSx,
            }}
          >
            {night.title}
          </Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <CalendarMonthIcon color={archived ? 'primary' : 'secondary'} sx={{ flex: '0 0 auto' }} />
              <Typography sx={textWrapSx}>
                {gatheringWeekday}, {gatheringDate}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <AccessTimeIcon color={archived ? 'primary' : 'secondary'} sx={{ flex: '0 0 auto' }} />
              <Typography sx={textWrapSx}>{gatheringTime}</Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <LocationOnIcon color={archived ? 'primary' : 'secondary'} sx={{ flex: '0 0 auto' }} />
              <Typography sx={textWrapSx}>
                {night.locationName}
                {night.locationAddress ? ` - ${night.locationAddress}` : ''}
              </Typography>
            </Stack>
          </Stack>
          <Typography
            variant="subtitle2"
            sx={{
              color: archived ? 'text.secondary' : 'rgba(255,248,232,0.72)',
              mt: 5,
              mb: 2,
              ...textWrapSx,
            }}
          >
            {t('nights.aroundTable')}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ pl: 1, minWidth: 0, flexWrap: 'wrap', rowGap: 1 }}
          >
            {tableAttendees.map((person) => (
              <Tooltip
                key={person.id || person.displayName || person.username}
                title={t(invitationStatusLabelKey(person.invitationStatus))}
              >
                <Avatar
                  src={person.avatarUrl || undefined}
                  alt={person.displayName || person.username || t('common.unknown')}
                  sx={{
                    ml: -1,
                    flex: '0 0 auto',
                    ...invitationAvatarSx(
                      person.invitationStatus,
                      archived ? 'rgba(255,250,240,0.92)' : 'rgba(255,248,232,0.88)'
                    ),
                  }}
                />
              </Tooltip>
            ))}
            {canEdit ? (
              <Tooltip title={t('nights.actions.addCompanions')}>
                <IconButton
                  aria-label={t('nights.actions.addCompanions')}
                  onClick={() => onNavigate(`/nights/${night.id}/edit?step=companions`)}
                  sx={{
                    ml: -1,
                    width: 40,
                    height: 40,
                    border: '2px dashed rgba(255,248,232,0.72)',
                    bgcolor: 'secondary.light',
                    color: 'primary.dark',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'secondary.main' },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          {awaitingResponse ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                disabled={responseLoading}
                onClick={() => onAcceptInvitation(night)}
              >
                {responseLoading ? t('nights.invitations.answering') : t('nights.invitations.accept')}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={responseLoading}
                onClick={() => onDeclineInvitation(night)}
                sx={{ color: archived ? 'text.primary' : 'inherit', borderColor: 'currentColor' }}
              >
                {t('nights.invitations.decline')}
              </Button>
            </Stack>
          ) : null}
        </Box>

        <Box sx={{ p: { xs: 3.5, md: 5 }, minWidth: 0, maxWidth: '100%' }}>
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                border: '1px solid',
                borderColor: 'rgba(236, 170, 65, 0.34)',
                borderRadius: 2,
                bgcolor: 'rgba(255, 250, 240, 0.52)',
                minWidth: 0,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, ...textWrapSx }}>
                {t('nights.whoBringsWhat')}
              </Typography>
              <NightContributionsPanel
                assignments={night.assignments || []}
                emptyLabel={t('nights.noAssignments')}
                language={language}
                t={t}
              />
            </Box>
            {!archived ? (
              <>
                {canVote ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      onNavigate(
                        `/nights/${night.id}/edit?step=games${canManageGames ? '' : '&mode=vote'}`
                      )
                    }
                    sx={{
                      width: '100%',
                      minWidth: 0,
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      py: 1.5,
                      px: { xs: 1.5, sm: 2 },
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      '& .MuiButton-startIcon': {
                        flex: '0 0 auto',
                      },
                    }}
                  >
                    {t('nights.actions.voteAnotherGame')}
                  </Button>
                ) : null}
                <Box
                  sx={{
                    mt: 1.5,
                    p: { xs: 1.5, sm: 2 },
                    border: '1px solid',
                    borderColor: 'rgba(15, 76, 69, 0.18)',
                    borderRadius: 2,
                    bgcolor: 'rgba(15, 76, 69, 0.035)',
                    minWidth: 0,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ...textWrapSx }}>
                    {t('nights.votes.summaryTitle')}
                  </Typography>
                  <NightVotesPanel
                    language={language}
                    night={night}
                    t={t}
                  />
                </Box>
                {canDownloadCalendarFile ? (
                  <Button
                    variant="contained"
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => onOpenChat?.(night)}
                    sx={{
                      width: '100%',
                      whiteSpace: 'normal',
                    }}
                  >
                    {t('chat.actions.openNight')}
                  </Button>
                ) : null}
                {canDownloadCalendarFile ? (
                  <Button
                    variant="outlined"
                    startIcon={<CalendarMonthIcon />}
                    disabled={calendarFileLoading}
                    onClick={() => onDownloadCalendarFile?.(night)}
                    sx={{
                      width: '100%',
                      whiteSpace: 'normal',
                    }}
                  >
                    {calendarFileLoading
                      ? t('integrations.calendarFile.downloading')
                      : t('integrations.calendarFile.addToCalendar')}
                  </Button>
                ) : null}
                {canUseCalendar ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ pt: 0.5 }}
                  >
                    <Button
                      variant={calendarEvent?.syncStatus === 'synced' ? 'outlined' : 'contained'}
                      color={calendar.needsReconnect ? 'warning' : 'primary'}
                      startIcon={<CalendarMonthIcon />}
                      disabled={calendarLoading || calendar.needsReconnect}
                      onClick={() => onSyncCalendar?.(night)}
                      sx={{
                        flex: 1,
                        whiteSpace: 'normal',
                      }}
                    >
                      {calendar.needsReconnect
                        ? t('integrations.googleCalendar.reconnectRequired')
                        : calendarEvent?.syncStatus === 'synced'
                          ? t('integrations.googleCalendar.syncedOnCard')
                          : t('integrations.googleCalendar.addToCalendar')}
                    </Button>
                    {calendarEvent ? (
                      <Button
                        variant="text"
                        color="inherit"
                        disabled={calendarLoading}
                        onClick={() => onRemoveCalendar?.(night)}
                      >
                        {t('integrations.googleCalendar.removeFromCalendar')}
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};

const NightsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedPage = Math.max(Number(query.get('page')) || 1, 1);
  const highlightedNightId = nightFocusIdFromQuery(query);
  const focusedFromInvitation = highlightedNightId === Number(query.get('focusNightId'));
  const [futureNights, setFutureNights] = useState([]);
  const [futurePage, setFuturePage] = useState(requestedPage);
  const [futureTotal, setFutureTotal] = useState(0);
  const [pastNights, setPastNights] = useState([]);
  const [pastPage, setPastPage] = useState(1);
  const [pastTotal, setPastTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(true);
  const [errorKey, setErrorKey] = useState('');
  const [errorContext, setErrorContext] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');
  const [nightToCancel, setNightToCancel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeHighlightedNightId, setActiveHighlightedNightId] = useState(null);
  const [focusFeedbackKey, setFocusFeedbackKey] = useState('');
  const [respondingNightId, setRespondingNightId] = useState(null);
  const [calendarLoadingNightId, setCalendarLoadingNightId] = useState(null);
  const [calendarFileLoadingNightId, setCalendarFileLoadingNightId] = useState(null);
  const [shareLoadingNightId, setShareLoadingNightId] = useState(null);
  const [chatContext, setChatContext] = useState(null);
  const highlightedRefs = useRef(new Map());
  const focusLookupRef = useRef('');
  const pastSectionRef = useRef(null);
  const shouldScrollToPastSectionRef = useRef(false);
  const language = i18n.resolvedLanguage || i18n.language;
  const hasGuests = (nightToCancel?.companions || []).length > 0;
  const pageCount = futurePageCount(futureTotal);
  const pastPages = pastPageCount(pastTotal);
  const pendingInvitationNights = futureNights.filter(
    (night) => night.invitations?.viewerStatus === 'pending'
  );
  const activeFutureNights = futureNights.filter(
    (night) => night.invitations?.viewerStatus !== 'pending'
  );
  const focusedNightLoaded = Boolean(
    highlightedNightId &&
      futureNights.some((night) => Number(night.id) === Number(highlightedNightId))
  );

  useEffect(() => {
    setFuturePage(requestedPage);
  }, [requestedPage]);

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setErrorKey('');
    setErrorContext(null);

    fetchNights({ scope: 'future', page: futurePage, limit: FUTURE_NIGHTS_PAGE_SIZE })
      .then((futureData) => {
        if (!mounted) return;

        const nextTotal = Number(futureData.total || 0);
        const nextPageCount = futurePageCount(nextTotal);

        if (nextTotal > 0 && futurePage > nextPageCount) {
          const focusParam = focusedFromInvitation ? 'focusNightId' : 'highlightNight';
          const focusSearch = highlightedNightId ? `&${focusParam}=${highlightedNightId}` : '';

          navigate(`/nights?page=${nextPageCount}${focusSearch}`, {
            replace: true,
            state: {},
          });
          return;
        }

        setFutureNights(normalizeNightListForRender(futureData.items));
        setFutureTotal(nextTotal);
      })
      .catch((error) => {
        if (mounted) {
          setErrorKey('nights.feedback.loadError');
          setErrorContext(supportContextFromApiError(error, { pageName: 'Game Nights' }));
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    focusedFromInvitation,
    futurePage,
    highlightedNightId,
    navigate,
    refreshKey,
  ]);

  useEffect(() => {
    let mounted = true;

    setPastLoading(true);

    fetchNights({ scope: 'past', page: pastPage, limit: PAST_NIGHTS_PAGE_SIZE })
      .then((pastData) => {
        if (!mounted) return;

        const nextTotal = Number(pastData.total || 0);
        const nextPageCount = pastPageCount(nextTotal);

        if (nextTotal > 0 && pastPage > nextPageCount) {
          setPastPage(nextPageCount);
          return;
        }

        setPastTotal(nextTotal);
        setPastNights(normalizeNightListForRender(pastData.items));
      })
      .catch((error) => {
        if (mounted) {
          setErrorKey('nights.feedback.loadError');
          setErrorContext(supportContextFromApiError(error, { pageName: 'Game Nights' }));
        }
      })
      .finally(() => {
        if (mounted) setPastLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [pastPage, refreshKey]);

  useEffect(() => {
    if (loading || !highlightedNightId) return;

    const element = highlightedRefs.current.get(highlightedNightId);
    if (!element) return;

    setActiveHighlightedNightId(highlightedNightId);
    setFocusFeedbackKey(focusedFromInvitation ? 'nights.feedback.invitationOpened' : '');

    const timerId = window.setTimeout(() => {
      element.scrollIntoView(NIGHT_FOCUS_SCROLL_OPTIONS);
    }, 120);
    const clearHighlightTimerId = window.setTimeout(() => {
      setActiveHighlightedNightId((current) =>
        current === highlightedNightId ? null : current
      );
    }, 4000);

    return () => {
      window.clearTimeout(timerId);
      window.clearTimeout(clearHighlightTimerId);
    };
  }, [focusedFromInvitation, focusedNightLoaded, highlightedNightId, loading]);

  useEffect(() => {
    if (!highlightedNightId) {
      setActiveHighlightedNightId(null);
      setFocusFeedbackKey('');
      focusLookupRef.current = '';
    }
  }, [highlightedNightId]);

  useEffect(() => {
    if (pastLoading || !shouldScrollToPastSectionRef.current) return;

    shouldScrollToPastSectionRef.current = false;
    window.requestAnimationFrame(() => {
      pastSectionRef.current?.scrollIntoView(PAST_SECTION_SCROLL_OPTIONS);
    });
  }, [pastLoading, pastNights]);

  useEffect(() => {
    if (!shouldRecoverFocusedNight({ focusedNightLoaded, highlightedNightId, loading })) {
      return;
    }

    const lookupKey = `${highlightedNightId}:${futurePage}:${futureTotal}`;
    if (focusLookupRef.current === lookupKey) return;
    focusLookupRef.current = lookupKey;

    let mounted = true;
    const focusParam = focusedFromInvitation ? 'focusNightId' : 'highlightNight';
    const recoverFocusedNight = async () => {
      try {
        const night = await fetchNight(highlightedNightId);
        const [normalized] = normalizeNightListForRender([night]);
        if (!mounted || !normalized) return false;

        setFutureNights((current) => mergeFocusedNightForRender(current, normalized));
        setFutureTotal((current) => Math.max(current, futureNights.length + 1));
        setActiveHighlightedNightId(highlightedNightId);
        setFocusFeedbackKey(focusedFromInvitation ? 'nights.feedback.invitationOpened' : '');

        return true;
      } catch {
        return false;
      }
    };
    const showFocusNotFound = () => {
      setFocusFeedbackKey(
        focusedFromInvitation
          ? 'nights.feedback.invitationNotFound'
          : 'nights.feedback.highlightNotFound'
      );
    };

    fetchNightPosition(highlightedNightId, {
      scope: 'future',
      limit: FUTURE_NIGHTS_PAGE_SIZE,
    })
      .then((position) => {
        if (!mounted) return;

        const targetPage = Math.max(Number(position?.page) || 1, 1);
        if (targetPage !== futurePage) {
          navigate(`/nights?page=${targetPage}&${focusParam}=${highlightedNightId}`, {
            replace: true,
            state: {},
          });
          return;
        }

        recoverFocusedNight().then((recovered) => {
          if (mounted && !recovered) showFocusNotFound();
        });
      })
      .catch(() => {
        if (!mounted) return;

        recoverFocusedNight().then((recovered) => {
          if (mounted && !recovered) showFocusNotFound();
        });
      });

    return () => {
      mounted = false;
    };
  }, [
    focusedNightLoaded,
    focusedFromInvitation,
    futurePage,
    futureNights.length,
    futureTotal,
    highlightedNightId,
    loading,
    navigate,
  ]);

  useEffect(() => {
    const nextToast = location.state?.toast;
    if (!nextToast) return;

    toast.success(nextToast);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate, toast]);

  useEffect(() => {
    const refreshNights = () => {
      setRefreshKey((current) => current + 1);
    };

    const handleServerEvent = (event) => {
      if (event.detail?.type?.startsWith('night.')) {
        refreshNights();
      }
    };

    window.addEventListener('thetavern:server-event', handleServerEvent);
    window.addEventListener('thetavern:revalidate', refreshNights);

    return () => {
      window.removeEventListener('thetavern:server-event', handleServerEvent);
      window.removeEventListener('thetavern:revalidate', refreshNights);
    };
  }, []);

  const openCancelDialog = (night) => {
    setNightToCancel(night);
    setCancelMessage('');
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    if (deleting) return;

    setCancelDialogOpen(false);
    setNightToCancel(null);
    setCancelMessage('');
  };

  const handleCancelNight = async (sendNotifications) => {
    if (!nightToCancel) return;

    setDeleting(true);
    setErrorKey('');

    try {
      await deleteNight(nightToCancel.id, {
        sendNotifications,
        message: cancelMessage.trim() || null,
      });

      if (futureNights.length === 1 && futurePage > 1) {
        setFuturePage((current) => current - 1);
      } else {
        setRefreshKey((current) => current + 1);
      }

      setCancelDialogOpen(false);
      setNightToCancel(null);
      setCancelMessage('');
      toast.success(t('nights.feedback.deleteSuccess'));
    } catch {
      toast.error(t('nights.feedback.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleInvitationResponse = async (night, action) => {
    if (!night?.id || respondingNightId) return;

    setRespondingNightId(night.id);
    try {
      if (action === 'accept') {
        const acceptedNight = await acceptNightInvitation(night.id);
        const [normalizedAcceptedNight] = normalizeNightListForRender([acceptedNight]);
        if (normalizedAcceptedNight) {
          setFutureNights((current) => mergeFocusedNightForRender(current, normalizedAcceptedNight));
        }
        toast.success(t('nights.feedback.invitationAccepted'));
      } else {
        await declineNightInvitation(night.id);
        toast.success(t('nights.feedback.invitationDeclined'));
      }
      setRefreshKey((current) => current + 1);
    } catch (error) {
      toast.error(nightInvitationResponseMessage(error, t));
      if ([404, 409].includes(Number(error?.response?.status))) {
        setRefreshKey((current) => current + 1);
      }
    } finally {
      setRespondingNightId(null);
    }
  };

  const mergeUpdatedNight = (night) => {
    const [normalizedNight] = normalizeNightListForRender([night]);
    if (!normalizedNight) return;

    setFutureNights((current) => mergeFocusedNightForRender(current, normalizedNight));
    setPastNights((current) =>
      current.map((item) => (item.id === normalizedNight.id ? normalizedNight : item))
    );
  };

  const handleCalendarSync = async (night) => {
    if (!night?.id || calendarLoadingNightId) return;

    setCalendarLoadingNightId(night.id);
    try {
      mergeUpdatedNight(await syncNightCalendar(night.id));
      toast.success(t('integrations.googleCalendar.syncSuccess'));
    } catch {
      toast.error(t('integrations.googleCalendar.syncError'));
    } finally {
      setCalendarLoadingNightId(null);
    }
  };

  const handleCalendarRemove = async (night) => {
    if (!night?.id || calendarLoadingNightId) return;

    setCalendarLoadingNightId(night.id);
    try {
      mergeUpdatedNight(await removeNightCalendar(night.id));
      toast.success(t('integrations.googleCalendar.removeSuccess'));
    } catch {
      toast.error(t('integrations.googleCalendar.removeError'));
    } finally {
      setCalendarLoadingNightId(null);
    }
  };

  const handleCalendarFileDownload = async (night) => {
    if (!night?.id || calendarFileLoadingNightId) return;

    setCalendarFileLoadingNightId(night.id);
    try {
      await downloadNightCalendarFile(night.id, night);
      toast.success(t('integrations.calendarFile.downloadSuccess'));
    } catch {
      toast.error(t('integrations.calendarFile.downloadError'));
    } finally {
      setCalendarFileLoadingNightId(null);
    }
  };

  const handleShareNight = async ({ night, title }) => {
    if (!night?.id || shareLoadingNightId) return;

    setShareLoadingNightId(night.id);
    try {
      const invitation = await createNightShareInvitation(night.id);
      const url = nightShareUrl(invitation.token);
      const text = nightShareText({
        inviterName: night.host?.displayName,
        t,
        title: night.title,
        url,
      });

      if (navigator.share) {
        try {
          await navigator.share({ title, text, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      await navigator.clipboard?.writeText(text);
      toast.success(t('nights.feedback.shareCopied'));
    } catch {
      toast.error(t('nights.feedback.shareError'));
    } finally {
      setShareLoadingNightId(null);
    }
  };

  const openNightChat = (night) => {
    const participants = [night.host, ...(night.companions || [])].filter(Boolean);

    setChatContext({
      type: 'night',
      nightId: night.id,
      title: t('chat.context.nightTitle', { title: night.title }),
      subtitle: t('chat.context.nightSubtitle'),
      participants,
    });
  };

  const handlePastPageChange = (_, page) => {
    shouldScrollToPastSectionRef.current = true;
    setPastPage(page);
  };

  return (
    <Stack spacing={5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
            {t('nights.eyebrow')}
          </Typography>
          <Typography variant="h1">{t('nights.title')}</Typography>
          <Typography color="text.secondary">
            {t('nights.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/nights/new')}
        >
          {t('nights.actions.newNight')}
        </Button>
      </Stack>

      {loading ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={260} />
              <Skeleton variant="rounded" height={180} />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {errorKey ? (
        <Alert
          severity="error"
          action={<ReportIssueButton context={errorContext} sx={{ whiteSpace: 'nowrap' }} />}
        >
          {t(errorKey)}
        </Alert>
      ) : null}

      {!errorKey && focusFeedbackKey ? (
        <Alert severity="info">{t(focusFeedbackKey)}</Alert>
      ) : null}

      {!loading && !errorKey && futureNights.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 3.5, md: 5 } }}>
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="h2">{t('nights.noUpcomingTitle')}</Typography>
              <Typography color="text.secondary">{t('nights.noUpcomingSubtitle')}</Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/nights/new')}
              >
                {t('nights.actions.newNight')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {!loading && !errorKey && futureNights.length > 0 ? (
        <Stack spacing={4}>
          {[
            {
              key: 'pending',
              title: t('nights.sections.pendingInvitations'),
              nights: pendingInvitationNights,
            },
            {
              key: 'accepted',
              title: t('nights.sections.acceptedInvitations'),
              nights: activeFutureNights,
            },
          ].map((section) =>
            section.nights.length > 0 ? (
              <Stack key={section.key} spacing={2.25}>
                <Typography variant="h3">{section.title}</Typography>
                {section.nights.map((night) => {
                  const index = futureNights.findIndex((item) => item.id === night.id);
                  const status = nightCardStatus({
                    index: Math.max(index, 0),
                    page: futurePage,
                    pageSize: FUTURE_NIGHTS_PAGE_SIZE,
                    scope: 'future',
                  });

                  return (
                    <Box
                      key={night.id}
                      ref={(element) => {
                        if (element) highlightedRefs.current.set(night.id, element);
                        else highlightedRefs.current.delete(night.id);
                      }}
                      sx={nightFocusScrollTargetSx}
                    >
                      <NightCard
                        archived={status.archived}
                        highlighted={night.id === activeHighlightedNightId}
                        labelKey={
                          night.invitations?.viewerStatus === 'pending'
                            ? 'nights.pendingGathering'
                            : status.labelKey
                        }
                        language={language}
                        night={night}
                        onAcceptInvitation={(item) => handleInvitationResponse(item, 'accept')}
                        onCancel={openCancelDialog}
                        onDeclineInvitation={(item) => handleInvitationResponse(item, 'decline')}
                        onShareNight={handleShareNight}
                        onRemoveCalendar={handleCalendarRemove}
                        onSyncCalendar={handleCalendarSync}
                        onDownloadCalendarFile={handleCalendarFileDownload}
                        onOpenChat={openNightChat}
                        onNavigate={navigate}
                        responseLoading={respondingNightId === night.id}
                        shareLoading={shareLoadingNightId === night.id}
                        calendarLoading={calendarLoadingNightId === night.id}
                        calendarFileLoading={calendarFileLoadingNightId === night.id}
                        t={t}
                      />
                    </Box>
                  );
                })}
              </Stack>
            ) : null
          )}

          {pageCount > 1 ? (
            <Stack alignItems="center">
              <Pagination
                page={futurePage}
                count={pageCount}
                color="primary"
                onChange={(_, page) => {
                  setFuturePage(page);
                  navigate(`/nights?page=${page}`, { replace: true });
                }}
              />
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3, bgcolor: 'background.default' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {hasGuests
            ? t('nights.cancelDialog.titleWithGuests')
            : t('nights.cancelDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: hasGuests ? 2 : 0 }}>
            {hasGuests
              ? t('nights.cancelDialog.subtitleWithGuests')
              : t('nights.cancelDialog.subtitle')}
          </Typography>
          {hasGuests ? (
            <TextField
              value={cancelMessage}
              onChange={(event) => setCancelMessage(event.target.value)}
              placeholder={t('nights.cancelDialog.messagePlaceholder')}
              multiline
              minRows={4}
              fullWidth
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button disabled={deleting} onClick={closeCancelDialog}>
            {t('nights.cancelDialog.backToEditing')}
          </Button>
          {hasGuests ? (
            <>
              <Button
                disabled={deleting}
                onClick={() => handleCancelNight(false)}
              >
                {t('nights.cancelDialog.doNotSend')}
              </Button>
              <Button
                variant="contained"
                disabled={deleting}
                onClick={() => handleCancelNight(true)}
              >
                {deleting ? t('nights.cancelDialog.cancelling') : t('nights.cancelDialog.send')}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              disabled={deleting}
              onClick={() => handleCancelNight(false)}
            >
              {deleting ? t('nights.cancelDialog.cancelling') : t('nights.cancelDialog.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Box ref={pastSectionRef} sx={nightFocusScrollTargetSx}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('nights.pastNightsTitle')}
        </Typography>
        {pastLoading ? (
          <Card variant="outlined">
            <CardContent>
              <Skeleton variant="rounded" height={220} />
            </CardContent>
          </Card>
        ) : loading || errorKey ? null : pastNights.length > 0 ? (
          <Stack spacing={3}>
            {pastNights.map((night, index) => {
              const status = nightCardStatus({
                index,
                page: pastPage,
                pageSize: PAST_NIGHTS_PAGE_SIZE,
                scope: 'past',
              });

              return (
                <NightCard
                  key={night.id}
                  archived={status.archived}
                  labelKey={status.labelKey}
                  language={language}
                  night={night}
                  onCancel={openCancelDialog}
                  onShareNight={handleShareNight}
                  onRemoveCalendar={handleCalendarRemove}
                  onSyncCalendar={handleCalendarSync}
                  onDownloadCalendarFile={handleCalendarFileDownload}
                  onOpenChat={openNightChat}
                  onNavigate={navigate}
                  calendarLoading={calendarLoadingNightId === night.id}
                  shareLoading={shareLoadingNightId === night.id}
                  calendarFileLoading={calendarFileLoadingNightId === night.id}
                  t={t}
                />
              );
            })}

            {pastPages > 1 ? (
              <Stack alignItems="center">
                <Pagination
                  page={pastPage}
                  count={pastPages}
                  color="primary"
                  onChange={handlePastPageChange}
                />
              </Stack>
            ) : null}
          </Stack>
        ) : (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 3,
              p: 5,
              textAlign: 'center',
              bgcolor: 'rgba(15, 76, 69, 0.04)',
            }}
          >
            <Typography variant="h3">{t('nights.emptyTitle')}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 420, mx: 'auto' }}>
              {t('nights.emptySubtitle')}
            </Typography>
          </Box>
        )}
      </Box>
      <TavernChatPanel
        context={chatContext || {}}
        open={Boolean(chatContext)}
        onClose={() => setChatContext(null)}
      />
    </Stack>
  );
};

export default NightsPage;
